import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiPhone, FiMail, FiArrowRight } from 'react-icons/fi';
import { FaWhatsapp, FaLinkedin, FaInstagram, FaTwitter } from 'react-icons/fa';
import LoginAlertModal from '../common/LoginAlertModal.jsx';

const Footer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const currentYear = new Date().getFullYear();

  const handleBecomeWorker = (e) => {
    if (!user) {
      e.preventDefault();
      setShowLoginAlert(true);
    }
  };

  const handleLoginClick = () => {
    setShowLoginAlert(false);
    navigate('/login');
  };

  return (
    <footer className="relative overflow-hidden">
      {/* Desktop Footer - High-Fidelity Website Style */}
      <div className="hidden md:block bg-slate-900 text-white border-t border-slate-800 relative">
        <LoginAlertModal
          isOpen={showLoginAlert}
          onClose={() => setShowLoginAlert(false)}
          onLoginClick={handleLoginClick}
        />

        {/* Subtle Gradient Backdrops */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-cherry-600/10 rounded-full blur-[100px] -mr-48 -mt-48 opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[100px] -ml-48 -mb-48 opacity-20"></div>

        <div className="container mx-auto px-6 py-16 md:py-24 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Brand Meta */}
            <div className="lg:col-span-4 space-y-6">
              <Link to="/" className="text-2xl font-black flex items-center gap-1 group">
                <span className="text-cherry-500 group-hover:text-amber-400 transition-colors">Namma</span>
                <span className="text-white">Eventz</span>
              </Link>
              <p className="text-slate-400 text-sm font-medium leading-relaxed max-w-sm">
                Building the gold standard digital infrastructure for the event-tech economy. Connecting verified specialists with premium demand.
              </p>
              <div className="flex gap-4">
                {[
                  { icon: <FaWhatsapp />, href: "https://wa.me/917871633001" },
                  { icon: <FaLinkedin />, href: "https://www.linkedin.com/in/namma-eventz-8496a23a5/" },
                  { icon: <FaInstagram />, href: "https://www.instagram.com/nammaeventzofficial/" },
                  { icon: <FaTwitter />, href: "https://x.com/nammaeventz" },
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-cherry-600 hover:text-white hover:border-cherry-600 transition-all duration-300"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation Nodes */}
            <div className="lg:col-span-2 space-y-6">
              <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Operational</h4>
              <ul className="space-y-3">
                <li><Link to="/workers" className="text-sm font-bold text-slate-300 hover:text-cherry-500 transition-all">Find Experts</Link></li>
                <li><Link to="/become-worker" onClick={handleBecomeWorker} className="text-sm font-bold text-slate-300 hover:text-cherry-500 transition-all">Join Network</Link></li>
                <li><Link to="/contact" className="text-sm font-bold text-slate-300 hover:text-cherry-500 transition-all">Support Desk</Link></li>
              </ul>
            </div>

            {/* Global Presence */}
            <div className="lg:col-span-3 space-y-6">
              <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Communication</h4>
              <div className="space-y-4">
                <a href="tel:+917871633001" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors group">
                  <FiPhone className="text-cherry-500 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-bold">+91 78716 33001</span>
                </a>
                <a href="mailto:nammaeventz.company@gmail.com" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors group">
                  <FiMail className="text-cherry-500 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-bold truncate">support@nammaeventz.com</span>
                </a>
              </div>
            </div>

            {/* High-Fidelity CTA */}
            <div className="lg:col-span-3 p-8 rounded-[2rem] bg-white/5 border border-white/10 group overflow-hidden relative">
              <div className="relative z-10 space-y-4">
                <h4 className="text-sm font-black text-white">Scale your Agency?</h4>
                <p className="text-xs text-slate-400 font-medium leading-relaxed">
                  Join our professional ecosystem and start receiving verified event leads today.
                </p>
                <Link
                  to="/become-worker"
                  className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-cherry-500 group-hover:text-amber-400 transition-colors"
                >
                  Get Priority Access <FiArrowRight />
                </Link>
              </div>
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-cherry-600/10 rounded-full blur-2xl -mr-12 -mb-12"></div>
            </div>
          </div>

          {/* Footer Baseline */}
          <div className="mt-12 md:mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                © {currentYear} DINMANO TECHNOLOGY GROUP
              </p>
              <div className="hidden md:block w-1 h-1 rounded-full bg-slate-700"></div>
              <div className="flex gap-4">
                <Link to="/privacy-policy" className="text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-widest transition-colors">Privacy</Link>
                <Link to="/terms-of-service" className="text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-widest transition-colors">Terms</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Footer - Premium App Legal Strip */}
      <div className="block md:hidden bg-white/[0.02] backdrop-blur-md border-t border-white/5 py-8 pb-36">
        <div className="container mx-auto px-6 text-center space-y-4">
          <div className="flex justify-center items-center gap-4 text-[10px] font-bold text-slate-500/60 uppercase tracking-[0.2em]">
            <Link to="/privacy-policy" className="hover:text-cherry-500 transition-colors">Privacy</Link>
            <div className="w-1 h-1 rounded-full bg-slate-700/50"></div>
            <Link to="/terms-of-service" className="hover:text-cherry-500 transition-colors">Terms</Link>
          </div>
          <p className="text-[9px] font-medium text-slate-500/40 uppercase tracking-[0.25em]">
            © {currentYear} DINMANO TECHNOLOGY GROUP
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
